import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Webhook,
  Plus,
  Edit,
  Trash2,
  Play,
  RefreshCw,
  Check,
  X,
  Clock,
  AlertTriangle,
  Eye,
  Copy,
  RotateCcw,
  Loader2,
  ExternalLink,
  Key,
  Send,
  History,
  Settings,
  CheckCircle,
  XCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  success: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
  pending: { color: 'bg-amber-100 text-amber-800', icon: Clock }
};

export default function AdminWebhooks() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [availableEvents, setAvailableEvents] = useState({});
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeliveriesDialog, setShowDeliveriesDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    isActive: true,
    description: '',
    headers: {}
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  
  useEffect(() => {
    fetchWebhooks();
    fetchEvents();
  }, []);
  
  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      toast.error('Failed to load webhooks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/admin/webhooks/events');
      setAvailableEvents(response.data.events || {});
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };
  
  const openCreateDialog = () => {
    setFormData({
      name: '',
      url: '',
      events: [],
      isActive: true,
      description: '',
      headers: {}
    });
    setShowCreateDialog(true);
  };
  
  const openEditDialog = (webhook) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events || [],
      isActive: webhook.isActive,
      description: webhook.description || '',
      headers: webhook.headers || {}
    });
    setShowEditDialog(true);
  };
  
  const openDeliveriesDialog = async (webhook) => {
    setSelectedWebhook(webhook);
    setShowDeliveriesDialog(true);
    await fetchDeliveries(webhook.id);
  };
  
  const fetchDeliveries = async (webhookId) => {
    setLoadingDeliveries(true);
    try {
      const response = await axios.get(`/api/admin/webhooks/${webhookId}/deliveries?limit=50`);
      setDeliveries(response.data.deliveries || []);
    } catch (error) {
      toast.error('Failed to load delivery history');
    } finally {
      setLoadingDeliveries(false);
    }
  };
  
  const createWebhook = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a webhook name');
      return;
    }
    if (!formData.url.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }
    if (formData.events.length === 0) {
      toast.error('Please select at least one event');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post('/api/admin/webhooks', formData);
      toast.success('Webhook created successfully');
      setShowCreateDialog(false);
      fetchWebhooks();
      
      // Show secret
      if (response.data.webhook?.secret) {
        setNewSecret(response.data.webhook.secret);
        setShowSecretDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setSaving(false);
    }
  };
  
  const updateWebhook = async () => {
    if (!formData.name.trim() || !formData.url.trim() || formData.events.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`/api/admin/webhooks/${selectedWebhook.id}`, formData);
      toast.success('Webhook updated successfully');
      setShowEditDialog(false);
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update webhook');
    } finally {
      setSaving(false);
    }
  };
  
  const deleteWebhook = async () => {
    try {
      await axios.delete(`/api/admin/webhooks/${selectedWebhook.id}`);
      toast.success('Webhook deleted');
      setShowDeleteDialog(false);
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };
  
  const testWebhook = async (webhook) => {
    setTesting(true);
    try {
      const response = await axios.post(`/api/admin/webhooks/${webhook.id}/test`);
      if (response.data.success) {
        toast.success('Test webhook delivered successfully');
      } else {
        toast.error(`Test failed: ${response.data.delivery?.error || 'Unknown error'}`);
      }
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test webhook');
    } finally {
      setTesting(false);
    }
  };
  
  const regenerateSecret = async (webhook) => {
    try {
      const response = await axios.post(`/api/admin/webhooks/${webhook.id}/regenerate-secret`);
      setNewSecret(response.data.secret);
      setSelectedWebhook(webhook);
      setShowSecretDialog(true);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to regenerate secret');
    }
  };
  
  const retryDelivery = async (deliveryId) => {
    try {
      const response = await axios.post(`/api/admin/webhooks/deliveries/${deliveryId}/retry`);
      if (response.data.success) {
        toast.success('Retry successful');
      } else {
        toast.error('Retry failed');
      }
      if (selectedWebhook) {
        fetchDeliveries(selectedWebhook.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry');
    }
  };
  
  const toggleEvent = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  // Incoming events state
  const [incomingEvents, setIncomingEvents] = useState([]);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [loadingIncoming, setLoadingIncoming] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchIncomingEvents = async () => {
    try {
      setLoadingIncoming(true);
      const [eventsRes, healthRes] = await Promise.all([
        axios.get('/api/admin/webhooks/incoming?source=lunacal&limit=50'),
        axios.get('/api/admin/webhooks/incoming/health/summary')
      ]);
      setIncomingEvents(eventsRes.data.events || []);
      setWebhookHealth(healthRes.data.health || null);
    } catch (error) {
      console.error('Failed to fetch incoming events:', error);
    } finally {
      setLoadingIncoming(false);
    }
  };

  return (
    <div data-testid="admin-webhooks-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Webhook className="w-6 h-6 text-primary" />
            Webhooks
          </h1>
          <p className="text-muted-foreground">Manage outgoing webhooks and view incoming Lunacal events</p>
        </div>
      </div>

      <Tabs defaultValue="incoming" onValueChange={(v) => { if (v === 'incoming') fetchIncomingEvents(); }}>
        <TabsList className="mb-6">
          <TabsTrigger value="incoming">Incoming (Lunacal)</TabsTrigger>
          <TabsTrigger value="outgoing">Outgoing Webhooks</TabsTrigger>
        </TabsList>

        {/* Incoming Events Tab */}
        <TabsContent value="incoming">
          {/* Health Widget */}
          {webhookHealth && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card><CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground">Events (24h)</p>
                <p className="text-2xl font-semibold">{webhookHealth.total24h}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4">
                <p className="text-xs text-emerald-600">Successful</p>
                <p className="text-2xl font-semibold text-emerald-700">{webhookHealth.success24h}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4">
                <p className="text-xs text-red-600">Failed</p>
                <p className="text-2xl font-semibold text-red-700">{webhookHealth.failed24h}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground">Last Event</p>
                <p className="text-sm font-medium">{webhookHealth.lastEventAt ? formatDateTime(webhookHealth.lastEventAt) : 'None'}</p>
              </CardContent></Card>
            </div>
          )}

          {/* Webhook URL Info */}
          <Card className="mb-6">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Lunacal Webhook URL</p>
                  <code className="text-xs text-muted-foreground">{window.location.origin}/api/webhooks/lunacal</code>
                </div>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/lunacal`); toast.success('URL copied'); }}>
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>

          {loadingIncoming ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : incomingEvents.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Webhook className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-semibold text-lg mb-2">No incoming events yet</h3>
                <p className="text-muted-foreground">Events will appear here once Lunacal sends booking webhooks.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Attendee</TableHead>
                      <TableHead>Booking ID</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incomingEvents.map(event => (
                      <TableRow key={event.id} className="cursor-pointer hover:bg-muted/50" onClick={() => {
                        axios.get(`/api/admin/webhooks/incoming/${event.id}`).then(res => setSelectedEvent(res.data.event)).catch(() => toast.error('Failed to load event'));
                      }}>
                        <TableCell>
                          <Badge variant={event.eventType?.includes('CANCEL') ? 'destructive' : event.eventType?.includes('CREATED') ? 'default' : 'secondary'} className="text-xs">
                            {(event.eventType || '').replace('BOOKING_', '')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{event.attendeeName || '-'}</p>
                            <p className="text-xs text-muted-foreground">{event.attendeeEmail || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">{event.lunacalBookingId?.slice(0, 12) || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{event.receivedAt ? formatDateTime(event.receivedAt) : '-'}</TableCell>
                        <TableCell>
                          {event.processingResult === 'success' ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">OK</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Error</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Outgoing Webhooks Tab */}
        <TabsContent value="outgoing">
          <div className="flex items-center justify-end gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={fetchWebhooks}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={openCreateDialog} data-testid="create-webhook-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Webhook
            </Button>
          </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : webhooks.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Webhook className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg mb-2">No webhooks configured</h3>
              <p className="text-muted-foreground mb-6">
                Set up webhooks to receive real-time notifications when events occur in your platform.
                </p>
                <Button onClick={openCreateDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Webhook
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {webhooks.map(webhook => (
              <Card key={webhook.id} data-testid={`webhook-card-${webhook.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{webhook.name}</h3>
                        <Badge variant={webhook.isActive ? "default" : "outline"}>
                          {webhook.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <ExternalLink className="w-4 h-4" />
                        <span className="font-mono text-xs break-all">{webhook.url}</span>
                      </div>
                      
                      {webhook.description && (
                        <p className="text-sm text-muted-foreground mb-3">{webhook.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-1 mb-4">
                        {webhook.events?.map(event => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {availableEvents[event] || event}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-1">
                          <Send className="w-4 h-4 text-muted-foreground" />
                          <span>{webhook.stats?.totalDeliveries || 0} deliveries</span>
                        </div>
                        <div className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>{webhook.stats?.successfulDeliveries || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span>{webhook.stats?.failedDeliveries || 0}</span>
                        </div>
                        {webhook.stats?.pendingDeliveries > 0 && (
                          <div className="flex items-center gap-1 text-amber-600">
                            <Clock className="w-4 h-4" />
                            <span>{webhook.stats.pendingDeliveries} pending</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => testWebhook(webhook)}
                        disabled={testing || !webhook.isActive}
                        data-testid={`test-webhook-${webhook.id}`}
                      >
                        {testing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeliveriesDialog(webhook)}
                        data-testid={`view-deliveries-${webhook.id}`}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(webhook)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedWebhook(webhook);
                          setShowDeleteDialog(true);
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      
      {/* Create/Edit Webhook Dialog */}
      <Dialog 
        open={showCreateDialog || showEditDialog} 
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setShowEditDialog(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {showCreateDialog ? 'Create Webhook' : 'Edit Webhook'}
            </DialogTitle>
            <DialogDescription>
              Configure your webhook endpoint and select events to subscribe to
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Name *</Label>
                <Input
                  placeholder="My Webhook"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="webhook-name-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">Status</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(v) => setFormData({...formData, isActive: v})}
                  />
                  <span className="text-sm">{formData.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Endpoint URL *</Label>
              <Input
                placeholder="https://your-domain.com/webhook"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                data-testid="webhook-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Must be a publicly accessible HTTPS endpoint
              </p>
            </div>
            
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea
                placeholder="What is this webhook for?"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
              />
            </div>
            
            <div>
              <Label className="mb-3 block">Events to Subscribe *</Label>
              <ScrollArea className="h-[200px] border rounded-lg p-4">
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(availableEvents).map(([key, label]) => (
                    <div
                      key={key}
                      className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleEvent(key)}
                    >
                      <Checkbox
                        checked={formData.events.includes(key)}
                        onCheckedChange={() => toggleEvent(key)}
                      />
                      <Label className="cursor-pointer text-sm">{label}</Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.events.length} event(s) selected
              </p>
            </div>
            
            {showEditDialog && selectedWebhook && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-sm">Webhook Secret</Label>
                  <p className="text-xs text-muted-foreground">
                    {selectedWebhook.secretMasked}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => regenerateSecret(selectedWebhook)}
                >
                  <Key className="w-4 h-4 mr-1" />
                  Regenerate
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={showCreateDialog ? createWebhook : updateWebhook}
              disabled={saving}
              data-testid="save-webhook-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                showCreateDialog ? 'Create Webhook' : 'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Deliveries Dialog */}
      <Dialog open={showDeliveriesDialog} onOpenChange={setShowDeliveriesDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Delivery History</DialogTitle>
            <DialogDescription>
              {selectedWebhook?.name} - Recent webhook deliveries
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingDeliveries ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : deliveries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No deliveries yet</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>HTTP</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveries.map(delivery => {
                      const status = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
                      const StatusIcon = status.icon;
                      
                      return (
                        <TableRow key={delivery.id}>
                          <TableCell className="text-xs font-mono">
                            {formatDateTime(delivery.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {delivery.event}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {delivery.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {delivery.httpStatus || '-'}
                          </TableCell>
                          <TableCell>
                            {delivery.attempts}
                          </TableCell>
                          <TableCell className="text-right">
                            {delivery.status === 'failed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => retryDelivery(delivery.id)}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliveriesDialog(false)}>
              Close
            </Button>
            <Button 
              variant="outline" 
              onClick={() => selectedWebhook && fetchDeliveries(selectedWebhook.id)}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedWebhook?.name}&rdquo;? This will also delete all delivery history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteWebhook}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Secret Display Dialog */}
      <Dialog open={showSecretDialog} onOpenChange={setShowSecretDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Webhook Secret
            </DialogTitle>
            <DialogDescription>
              Save this secret securely. It will be used to verify webhook signatures.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono break-all">{newSecret}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(newSecret)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This secret will only be shown once. Make sure to copy it now.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              setShowSecretDialog(false);
              setNewSecret(null);
            }}>
              I&apos;ve Saved It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incoming Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Event Detail</DialogTitle>
            <DialogDescription>
              {selectedEvent?.eventType} — {selectedEvent?.receivedAt ? formatDateTime(selectedEvent.receivedAt) : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Event Type</Label>
                  <p className="font-medium">{selectedEvent.eventType}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Processing Result</Label>
                  <p className="font-medium">{selectedEvent.processingResult}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Attendee</Label>
                  <p className="font-medium">{selectedEvent.attendeeName || '-'}</p>
                  <p className="text-sm text-muted-foreground">{selectedEvent.attendeeEmail || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Booking ID</Label>
                  <p className="font-mono text-sm">{selectedEvent.lunacalBookingId || '-'}</p>
                </div>
                {selectedEvent.startTime && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Time</Label>
                    <p className="text-sm">{selectedEvent.startTime}</p>
                  </div>
                )}
                {selectedEvent.eventTitle && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Event Title</Label>
                    <p className="text-sm">{selectedEvent.eventTitle}</p>
                  </div>
                )}
              </div>
              {selectedEvent.rawPayload && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Raw Payload</Label>
                  <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto max-h-60">{JSON.stringify(selectedEvent.rawPayload, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>
      </Tabs>
    </div>
  );
}
